import type { SkillInvocationListItem } from '../../lib/api-client';

import { StatusBadge, formatDuration, relativeTime } from './skill-invocation-helpers';

interface Props {
  invocations: SkillInvocationListItem[];
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string | undefined;
  /**
   * Click a row to jump to the Skill Execution History tab with this row
   * pre-selected. The parent owns both the tab state and the selected
   * invocation id.
   */
  onOpenInExecutionsTab: (invocationId: string) => void;
}

/**
 * Compact list of the 10 most recent invocations of the current skill.
 * Rendered below the frontmatter block on the "Skill Details" tab. Clicking
 * a row navigates to the deeper view rather than expanding in place — this
 * surface intentionally stays scannable.
 */
export function SkillExecutionPreviewList({
  invocations,
  status,
  error,
  onOpenInExecutionsTab,
}: Props) {
  return (
    <div style={{ background: 'var(--surface)' }}>
      <div
        style={{
          padding: '12px 20px',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.5,
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          borderBottom: '1px solid var(--border)',
        }}
      >
        Recent executions
      </div>

      {status === 'loading' && invocations.length === 0 && (
        <Empty>Loading executions…</Empty>
      )}
      {status === 'error' && <Empty error>{error ?? 'Failed to load executions.'}</Empty>}
      {status === 'success' && invocations.length === 0 && (
        <Empty>No executions yet. Trigger this skill from a conversation to see it here.</Empty>
      )}

      {invocations.length > 0 && (
        <div role="list">
          {invocations.map((inv) => (
            <button
              key={inv.id}
              type="button"
              role="listitem"
              onClick={() => onOpenInExecutionsTab(inv.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                padding: '10px 20px',
                background: 'transparent',
                border: 'none',
                borderTop: '1px solid var(--border)',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'var(--text)',
                fontFamily: 'inherit',
                fontSize: 12,
              }}
            >
              <StatusBadge status={inv.status} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {inv.user_name ?? inv.user_email ?? inv.user_id}
                <span style={{ color: 'var(--text-mute)' }}>
                  {inv.conversation_title ? ` · in “${inv.conversation_title}”` : ''}
                </span>
              </span>
              <span style={{ color: 'var(--text-mute)', whiteSpace: 'nowrap' }}>
                {formatDuration(inv.duration_ms)}
              </span>
              <span style={{ color: 'var(--text-mute)', whiteSpace: 'nowrap' }}>
                {relativeTime(inv.started_at)}
              </span>
              <span style={{ color: 'var(--text-mute)' }} aria-hidden>
                ›
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Empty({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        padding: '16px 20px',
        fontSize: 12,
        color: error ? 'var(--kind-failure)' : 'var(--text-mute)',
      }}
    >
      {children}
    </div>
  );
}
